import REGEX from "@/config/regex";
import useProfile from "@/hooks/use-profile";
import { E_LANDING_PAGE_TYPE } from "@/utilities/types/LandingPage";
import { Roles } from "@/utilities/types/Users";
import cn from "@/utils/class-names";
import { zodResolver } from "@hookform/resolvers/zod";
import { isEmpty, omitBy } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { BiPlanet, BiRocket } from "react-icons/bi";
import { BsQuestionCircle } from "react-icons/bs";
import { MdAutoMode } from "react-icons/md";
import {
  Badge,
  Button,
  Input,
  Modal,
  RadioGroup,
  Select,
  SelectOption,
  Tooltip,
} from "rizzui";
import { z } from "zod";
import { useDomainManagement } from "./hooks/useDomainManagement";
import { DOMAIN_ERROR_MESSAGES, DOMAIN_REGEX } from "../config";
import { PiChecksBold, PiCopySimple } from "react-icons/pi";
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard";
import { CustomizeDomainState } from "../grid-listing/types";

const createDomainSchema = (
  type: E_LANDING_PAGE_TYPE,
  suffixDomains: string[],
) =>
  z.object({
    domain: z
      .string()
      .min(1, DOMAIN_ERROR_MESSAGES.REQUIRED)
      .refine(
        (val) => {
          if (type === E_LANDING_PAGE_TYPE.CUSTOM) {
            // Check if it's a valid domain format AND not one of the suffix domains
            return (
              REGEX.CHECK_DOMAIN.test(val) &&
              !suffixDomains.some((s) => val.endsWith(s))
            );
          } else {
            // For subdomain, only allow alphanumeric and hyphens, not allow uppercase
            return REGEX.CHECK_SUBDOMAIN.test(val);
          }
        },
        (val) => {
          if (type === E_LANDING_PAGE_TYPE.CUSTOM) {
            if (suffixDomains.some((s) => val.endsWith(s))) {
              return {
                message:
                  DOMAIN_ERROR_MESSAGES.SUFFIX_NOT_ALLOWED(suffixDomains),
              };
            }

            if (!REGEX.CHECK_DOMAIN.test(val)) {
              return { message: DOMAIN_ERROR_MESSAGES.INVALID_CUSTOM_DOMAIN };
            }
          }
          // Default message for subdomain or other cases
          return {
            message: DOMAIN_ERROR_MESSAGES.INVALID_SUBDOMAIN,
          };
        },
      ),
    domainName: z.string().min(1, "Name is required"),
    cNameTargetDomain: z.string().optional(),
    cNameTarget: z.string().optional(),
    selectedSuffix: z.string().min(1, "Suffix domain is required"),
  });

export type CustomizeDomainForm = {
  domain?: string;
  domainName: string;
  selectedSuffix: string;
  type?: E_LANDING_PAGE_TYPE;
  subDomain?: string;
  customDomain?: string;
  cNameTargetDomain?: string;
  cNameTarget?: string;
  subDomainWithoutSuffix?: string;
};

interface DomainConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: E_LANDING_PAGE_TYPE;
  onSubmit: (values: CustomizeDomainForm) => void;
  suffixDomains: string[];
  defaultSuffixDomain: string;
  initialValues?: Partial<CustomizeDomainForm>;
  updateDomain?: boolean;
  projectId?: string;
  disabledChangeDomain?: boolean;
  domainLabelQuestionTooltip?: React.ReactNode;
  isInactiveUser?: boolean;
  openDNSDomainModal?: (data: CustomizeDomainState) => void;
  customizeDomain?: CustomizeDomainState | null;
}

export default function DomainConfigModal({
  isOpen,
  onClose,
  type: initialType,
  onSubmit,
  suffixDomains,
  defaultSuffixDomain,
  initialValues,
  updateDomain,
  projectId,
  disabledChangeDomain,
  domainLabelQuestionTooltip,
  isInactiveUser = false,
  openDNSDomainModal,
  customizeDomain,
}: DomainConfigModalProps) {
  const [type, setType] = useState<E_LANDING_PAGE_TYPE>(initialType);

  const [isCopied, setIsCopied] = useState(false);
  const [, copyToClipboard] = useCopyToClipboard();

  const { profile } = useProfile();
  const isAdmin = profile?.role === Roles.ADMIN;
  console.log({ suffixDomains });

  // Use the domain management hook
  const {
    isGenerating,
    isCheckingDomain,
    checkDomainUniqueness,
    generateSubdomain,
    generateCnameTargetDomain,
    generateCnameTargetDomains,
    debouncedCheckDomain,
  } = useDomainManagement({
    projectId,
    suffixDomains,
  });

  const defaultValues: CustomizeDomainForm = useMemo(() => {
    const values = {
      domain: "",
      domainName: "",
      selectedSuffix: defaultSuffixDomain,
      ...initialValues,
    };

    // If it's a subdomain, remove the suffix from the initial domain
    if (values.subDomain) {
      for (const suffix of suffixDomains) {
        if (values.subDomain.endsWith(suffix)) {
          const withoutSuffixDomain =
            values?.subDomain?.slice(0, -suffix.length) || "";
          values.selectedSuffix = suffix; // default suffix
          values.subDomainWithoutSuffix = withoutSuffixDomain; // default subdomain without suffix
          break;
        }
      }
    }

    if (initialValues?.type === E_LANDING_PAGE_TYPE.SUBDOMAIN) {
      values.domain = values?.subDomainWithoutSuffix || "";
    } else {
      values.domain = values?.customDomain || "";
    }

    return values;
  }, [initialValues, suffixDomains, defaultSuffixDomain]);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    setError,
    clearErrors,
    resetField,
    getValues,
    formState: { errors, dirtyFields },
  } = useForm<CustomizeDomainForm>({
    resolver: zodResolver(createDomainSchema(type, suffixDomains)),
    mode: "onChange",
    defaultValues,
  });

  useEffect(() => {
    if (!initialType) return;
    setType(initialType);
  }, [initialType]);

  const selectedSuffix = watch("selectedSuffix");
  const cNameTargetDomain = watch("cNameTargetDomain");

  const isInActiveLdp = useMemo(() => {
    return isAdmin ? isInactiveUser : profile?.statusAccount === "inactive";
  }, [isAdmin, isInactiveUser, profile?.statusAccount]);

  // Handle domain uniqueness check with debounce
  const handleDebouncedCheckDomain = useCallback(
    (input: string, customSuffix?: string) => {
      const hasZodErrors = !isEmpty(
        omitBy(errors, (error) => error?.type === "manual"),
      );
      const shouldIgnore =
        !input || input === defaultValues?.domain || hasZodErrors;

      // If the input is the same as the default domain or another error exists from zod,
      // we should ignore the check
      if (shouldIgnore) return;

      // Clear previous manual error before re-checking
      clearErrors("domain");

      debouncedCheckDomain(
        input,
        type,
        (message) => {
          setError("domain", {
            type: "manual",
            message,
          });
        },
        customSuffix || getValues("selectedSuffix"),
      );
    },
    [
      debouncedCheckDomain,
      defaultValues?.domain,
      setError,
      clearErrors,
      type,
      errors,
      getValues,
    ],
  );

  const handleGenerateDomain = useCallback(async () => {
    const subDomain = await generateSubdomain();

    if (subDomain) {
      setValue("domain", subDomain, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue("domainName", subDomain, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }, [generateSubdomain, setValue]);

  const handleFormSubmit = useCallback(
    async (values: CustomizeDomainForm) => {
      // double check domain uniqueness before submitting
      console.log(values);

      const isNotChanged =
        values.domain === defaultValues?.domain &&
        values.selectedSuffix === defaultValues?.selectedSuffix &&
        type === initialType &&
        updateDomain;

      // If domain is not changed, we still need to process custom domains to show DNS guide
      if (isNotChanged && type !== E_LANDING_PAGE_TYPE.CUSTOM) {
        onClose();
        return;
      }

      // Ensure domain is not undefined before checking uniqueness
      if (!values.domain) {
        setError("domain", {
          type: "manual",
          message: DOMAIN_ERROR_MESSAGES.REQUIRED,
        });
        return;
      }

      if (
        dirtyFields.domain ||
        dirtyFields.selectedSuffix ||
        type !== initialType
      ) {
        // only check if the domain has changed
        const isUnique = await checkDomainUniqueness(
          values.domain,
          type,
          values.selectedSuffix,
        );
        if (!isUnique) {
          setError("domain", {
            type: "manual",
            message: DOMAIN_ERROR_MESSAGES.ALREADY_TAKEN,
          });
          return;
        }
      }

      // Prepare submission data based on domain type
      const submissionData: CustomizeDomainForm = {
        ...values,
        type,
      };

      // For subdomain type
      if (type === E_LANDING_PAGE_TYPE.SUBDOMAIN) {
        submissionData.subDomain = `${values.domain}${values.selectedSuffix}`;
        // The API needs domain for backward compatibility
        submissionData.domain = submissionData.subDomain;
      }
      // For custom domain type
      else {
        submissionData.customDomain = values.domain;
        submissionData.cNameTargetDomain = values.cNameTargetDomain || ""; // Empty string as requested
        // The API needs domain for backward compatibility
        submissionData.domain = values.domain;
        submissionData.cNameTarget = values.cNameTarget || "";
      }

      onSubmit(submissionData);
    },
    [
      checkDomainUniqueness,
      defaultValues?.domain,
      defaultValues?.selectedSuffix,
      dirtyFields.domain,
      dirtyFields.selectedSuffix,
      initialType,
      onClose,
      onSubmit,
      setError,
      clearErrors,
      type,
      updateDomain,
    ],
  );

  const handleTypeChange = useCallback(
    (newType: E_LANDING_PAGE_TYPE) => {
      setType(newType);
      resetField("domain", {
        defaultValue:
          newType === E_LANDING_PAGE_TYPE.SUBDOMAIN
            ? defaultValues?.subDomainWithoutSuffix || ""
            : defaultValues?.customDomain || "",
      });
      if (
        initialType === E_LANDING_PAGE_TYPE.CUSTOM &&
        newType === initialType &&
        customizeDomain?.customDomain &&
        openDNSDomainModal
      ) {
        openDNSDomainModal(customizeDomain);
      }
    },
    [
      defaultValues?.customDomain,
      defaultValues?.subDomainWithoutSuffix,
      resetField,
      initialValues,
    ],
  );

  const handleSuffixChange = useCallback(
    (option: SelectOption) => {
      setValue("selectedSuffix", option.value as string, {
        shouldValidate: true,
      });

      // Re-check domain uniqueness with new suffix
      const domain = watch("domain");
      if (domain) {
        handleDebouncedCheckDomain(domain, option.value as string);
      }
    },
    [handleDebouncedCheckDomain, setValue, watch],
  );

  useEffect(() => {
    if (type === E_LANDING_PAGE_TYPE.CUSTOM && !cNameTargetDomain) {
      // generateCnameTargetDomain().then((cNameTargetDomain) => {
      //   setValue("cNameTargetDomain", cNameTargetDomain, {
      //     shouldValidate: true,
      //     shouldDirty: true,
      //     shouldTouch: true,
      //   });
      // });
      generateCnameTargetDomains().then(
        ({ cNameTarget, cNameTargetDomain }) => {
          setValue("cNameTargetDomain", cNameTargetDomain, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
          });
          setValue("cNameTarget", cNameTarget, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
          });
        },
      );
    }
  }, [type, cNameTargetDomain, generateCnameTargetDomains, setValue]);

  const handleCopyToClipboard = useCallback(
    (value: string) => {
      copyToClipboard(value);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    },
    [copyToClipboard],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="m-auto px-7 pt-6 pb-8">
        <div className="mb-7 flex items-center justify-between">
          <h2 className="text-xl font-semibold">
            {type === E_LANDING_PAGE_TYPE.CUSTOM
              ? "Custom Domain"
              : "Subdomain"}{" "}
            Configuration
          </h2>
        </div>
        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          className="flex flex-col gap-4"
        >
          {updateDomain && (
            <div className="">
              <RadioGroup
                value={type}
                setValue={(e) => setType(e as E_LANDING_PAGE_TYPE)}
                className="grid grid-cols-1 sm:grid-cols-2 max-w-2xl mx-auto gap-4"
              >
                <Button
                  size="sm"
                  onClick={() => handleTypeChange(E_LANDING_PAGE_TYPE.CUSTOM)}
                  variant={
                    type === E_LANDING_PAGE_TYPE.CUSTOM ? "solid" : "outline"
                  }
                >
                  <BiPlanet className="size-4 mr-2" />
                  Custom Domain
                </Button>
                <Button
                  size="sm"
                  onClick={() =>
                    handleTypeChange(E_LANDING_PAGE_TYPE.SUBDOMAIN)
                  }
                  variant={
                    type === E_LANDING_PAGE_TYPE.SUBDOMAIN ? "solid" : "outline"
                  }
                >
                  <BiRocket className="size-4 mr-2" />
                  Subdomain
                </Button>
              </RadioGroup>
            </div>
          )}

          <div className="flex gap-2">
            <div className="flex-1">
              <Controller
                name="domain"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <div className="flex-1 relative">
                    <Input
                      label={
                        <div className="flex items-center gap-2">
                          Domain
                          {disabledChangeDomain &&
                            domainLabelQuestionTooltip && (
                              <Tooltip content={domainLabelQuestionTooltip}>
                                <button
                                  type="button"
                                  className="flex size-5 items-center justify-center "
                                >
                                  <BsQuestionCircle className="size-4" />
                                </button>
                              </Tooltip>
                            )}
                        </div>
                      }
                      placeholder={
                        type === E_LANDING_PAGE_TYPE.SUBDOMAIN
                          ? "Enter subdomain"
                          : "Enter your domain"
                      }
                      disabled={disabledChangeDomain}
                      onChange={async (e) => {
                        const input = e.target.value;

                        // Remove the suffix if user pastes full domain
                        if (
                          type === E_LANDING_PAGE_TYPE.SUBDOMAIN &&
                          suffixDomains.some((suffix) => input.endsWith(suffix))
                        ) {
                          const suffix = suffixDomains.find((s) =>
                            input.endsWith(s),
                          );
                          if (suffix) {
                            const domainWithoutSuffix = input.slice(
                              0,
                              -suffix.length,
                            );
                            onChange(domainWithoutSuffix);
                            setValue("selectedSuffix", suffix);
                            setValue("domainName", domainWithoutSuffix);

                            if (domainWithoutSuffix) {
                              handleDebouncedCheckDomain(domainWithoutSuffix);
                            }
                          }
                        } else {
                          onChange(input);
                          setValue("domainName", input);
                          handleDebouncedCheckDomain(input);
                        }
                      }}
                      value={value}
                      suffix={
                        type === E_LANDING_PAGE_TYPE.SUBDOMAIN ? (
                          <Controller
                            name="selectedSuffix"
                            control={control}
                            render={({ field }) => (
                              <Select
                                options={suffixDomains.map((suffix) => ({
                                  label: suffix,
                                  value: suffix,
                                }))}
                                value={field.value}
                                displayValue={(value: string) => {
                                  return (
                                    <Badge rounded="md" size="sm">
                                      {value}
                                    </Badge>
                                  );
                                }}
                                onChange={(option: SelectOption) =>
                                  handleSuffixChange(option)
                                }
                                className={cn(
                                  "min-w-max",
                                  // "pointer-events-none" // for now
                                )}
                                selectClassName="hover:!border-0 hover:!outline-0 p-0 h-fit"
                                dropdownClassName="min-w-max"
                                variant="text"
                                size="sm"
                                suffix=""
                              />
                            )}
                          />
                        ) : undefined
                      }
                      error={errors.domain?.message}
                    />
                  </div>
                )}
              />
            </div>

            {type === E_LANDING_PAGE_TYPE.SUBDOMAIN && (
              <Tooltip content="Generate unique subdomain">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGenerateDomain}
                  isLoading={isGenerating}
                  className="mt-6"
                  disabled={
                    isGenerating || isCheckingDomain || disabledChangeDomain
                  }
                >
                  <MdAutoMode />
                </Button>
              </Tooltip>
            )}
          </div>

          {/* {type === E_LANDING_PAGE_TYPE.CUSTOM && (
            <div className="flex flex-col">
              <div className="text-sm font-medium">CNAME</div>
              <div className="flex justify-between">
                <div className="flex items-center">
                  <Tooltip content={cNameTargetDomain}>
                    <span className="truncate">{cNameTargetDomain}</span>
                  </Tooltip>
                </div>
                <Button
                  variant="text"
                  size="sm"
                  onClick={() =>
                    cNameTargetDomain &&
                    handleCopyToClipboard(cNameTargetDomain)
                  }
                  className="p-0 m-0"
                >
                  {isCopied ? (
                    <PiChecksBold className="h-5 w-5 text-green-500" />
                  ) : (
                    <PiCopySimple className="h-5 w-5" />
                  )}
                </Button>
              </div>
            </div>
          )} */}

          {isInActiveLdp && (
            <p className="text-sm text-rose-500">
              Your plan has expired. Please contact ProjectSG to renew your
              package. Click{" "}
              <a
                className="font-bold underline"
                target="_blank"
                href="https://wa.me/projectsg"
              >
                here
              </a>{" "}
              to contact us.
            </p>
          )}

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                isGenerating ||
                isCheckingDomain ||
                (updateDomain && disabledChangeDomain)
              }
            >
              {isInActiveLdp ? "Save as Draft" : "Publish"}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
